import { useSetAtom } from "jotai";
import { useCallback } from "react";

import {
	$gitBranchAnalyze,
	$gitBranchDelete,
	$gitBranchNew,
	$gitBranchSwitch,
	$gitBranchTrack,
	$gitCommit,
	$gitDescribe,
	$gitFetch,
	$gitMoveCommit,
	$gitPull,
	$gitPush,
	$gitRebaseBookmark,
	$gitRemoteAdd,
	$gitResolveConflict,
	$gitRestoreToParent,
	$gitStashPop,
	$gitStashSave,
	$gitUndo,
	type GitBranchAnalyzeInput,
	type GitBranchDeleteInput,
	type GitBranchNewInput,
	type GitBranchSwitchInput,
	type GitBranchTrackInput,
	type GitCommitInput,
	type GitDescribeInput,
	type GitFetchInput,
	type GitMoveCommitInput,
	type GitPushInput,
	type GitRebaseBookmarkInput,
	type GitRemoteAddInput,
	type GitResolveConflictInput,
	type GitRestoreToParentInput,
	type GitStashPopInput,
	type GitStashSaveInput,
	type GitUndoInput,
} from "../../internal/git";

/**
 * Hook to access all git operations for a document
 * @param documentId - The document ID
 * @returns Object containing all git operation functions
 */
export const useGitOperations = (documentId: string | undefined) => {
	// Get all setter functions (mutations only - read operations are accessed via useAtomValue)
	const setCommit = useSetAtom($gitCommit(documentId));
	const setDescribe = useSetAtom($gitDescribe(documentId));
	const setFetch = useSetAtom($gitFetch);
	const setPull = useSetAtom($gitPull(documentId));
	const setPush = useSetAtom($gitPush);
	const setRemoteAdd = useSetAtom($gitRemoteAdd(documentId));

	const setBranchNew = useSetAtom($gitBranchNew(documentId));
	const setBranchSwitch = useSetAtom($gitBranchSwitch);
	const setMoveCommit = useSetAtom($gitMoveCommit);
	const setBranchDelete = useSetAtom($gitBranchDelete(documentId));
	const setBranchTrack = useSetAtom($gitBranchTrack);

	const setBranchAnalyze = useSetAtom($gitBranchAnalyze(documentId));

	const setStashSave = useSetAtom($gitStashSave(documentId));
	const setStashPop = useSetAtom($gitStashPop(documentId));
	const setResolveConflict = useSetAtom($gitResolveConflict(documentId));
	const setRestoreToParent = useSetAtom($gitRestoreToParent(documentId));
	const setUndo = useSetAtom($gitUndo(documentId));
	const setRebaseBookmark = useSetAtom($gitRebaseBookmark);

	// Wrap setters in callbacks
	const commit = useCallback(
		(input: GitCommitInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setCommit(input);
		},
		[documentId, setCommit]
	);

	const describe = useCallback(
		(input: GitDescribeInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setDescribe(input);
		},
		[documentId, setDescribe]
	);

	const fetch = useCallback(
		(input?: GitFetchInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setFetch({
				documentId,
				input: input || {},
			});
		},
		[documentId, setFetch]
	);

	const pull = useCallback(() => {
		if (!documentId) {
			return Promise.reject(new Error("No document ID"));
		}
		return setPull();
	}, [documentId, setPull]);

	const push = useCallback(
		(input?: GitPushInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setPush({
				documentId,
				input: input || {},
			});
		},
		[documentId, setPush]
	);

	const remoteAdd = useCallback(
		(input: GitRemoteAddInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setRemoteAdd(input);
		},
		[documentId, setRemoteAdd]
	);

	const branchNew = useCallback(
		(input: GitBranchNewInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setBranchNew(input);
		},
		[documentId, setBranchNew]
	);

	const branchSwitch = useCallback(
		(input: GitBranchSwitchInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setBranchSwitch({ input, documentId });
		},
		[documentId, setBranchSwitch]
	);

	const moveCommit = useCallback(
		(input: GitMoveCommitInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setMoveCommit({ input, documentId });
		},
		[documentId, setMoveCommit]
	);

	const branchDelete = useCallback(
		(input: GitBranchDeleteInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setBranchDelete(input);
		},
		[documentId, setBranchDelete]
	);

	const branchTrack = useCallback(
		(input: GitBranchTrackInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setBranchTrack({ input, documentId });
		},
		[documentId, setBranchTrack]
	);

	const branchAnalyze = useCallback(
		(input: GitBranchAnalyzeInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setBranchAnalyze(input);
		},
		[documentId, setBranchAnalyze]
	);

	const stashSave = useCallback(
		(input?: GitStashSaveInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setStashSave(input);
		},
		[documentId, setStashSave]
	);

	const stashPop = useCallback(
		(input: GitStashPopInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setStashPop(input);
		},
		[documentId, setStashPop]
	);

	const resolveConflict = useCallback(
		(input: GitResolveConflictInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setResolveConflict(input);
		},
		[documentId, setResolveConflict]
	);

	const restoreToParent = useCallback(
		(input: GitRestoreToParentInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setRestoreToParent(input);
		},
		[documentId, setRestoreToParent]
	);

	const undo = useCallback(
		(input: GitUndoInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setUndo(input);
		},
		[documentId, setUndo]
	);

	const rebaseBookmark = useCallback(
		(input: GitRebaseBookmarkInput) => {
			if (!documentId) {
				return Promise.reject(new Error("No document ID"));
			}
			return setRebaseBookmark({ documentId, ...input });
		},
		[documentId, setRebaseBookmark]
	);

	return {
		// Core operations
		commit,
		describe,

		// Remote operations
		fetch,
		pull,
		push,
		remoteAdd,

		// Branch operations
		branchNew,
		branchSwitch,
		moveCommit,
		branchDelete,
		branchTrack,
		branchAnalyze,
		rebaseBookmark,

		// Stash operations
		stashSave,
		stashPop,

		// Conflict resolution (JJ-specific)
		resolveConflict,
		restoreToParent,
		undo,
	};
};
